import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import RequestState from './RequestState';
import { fetchCompatibilityTargetSyllabuses, fetchStudentRecordsSyllabuses, fetchHubspotContact, fetchHubspotDeal, fetchTargetSyllabusActivities, resetCardinality, resetCompatibilityTargetSyllabuses, resetSourceActivities, saveRequest, saveStudent } from './requestSlice';
import STEPS from './Request/STEPS';
import EquivalencyRequest from '../../models/EquivalencyRequest';
import Syllabus, { CompatibleSyllabusSearchParams, SuggestedSyllabus } from '../../models/Syllabus';
import Institution from '../../models/Institution';
import StudentRecord, { StudentRecordAddress } from '../../models/StudentRecord';
import Student from '../../models/Student';
import { RecaptchaService } from '../../shared/google-recaptcha-v3/useRecaptcha';
import { Location } from 'react-router-dom';
import { useDataValidations, UseDataValidations } from '../../shared/hooks/useDataValidations';
import { SettingStore } from '../Settings/settingsSlice';
import Profile from '../../models/Profile';
import AppFile from '../../models/AppFile';
import Activity from '../../models/Activity';
import { useAppSelector } from '../../shared/redux/store';
import { HubspotContactParams } from '../../models/HubspotContact';
import DealData from '../../models/DealData';
import { Proposal } from '../../models/Proposal';
import { RuleDniValidationInterface } from '../../shared/hooks/useDniValidation';
import { useAppContext } from '../App.Context';
import { useRequestType } from '../../shared/hooks/useRequestType';

export interface RequestContext {
  source_institution: null | undefined | Institution,
  source_syllabus: null | Syllabus,
  target_syllabus: null | undefined | Syllabus | SuggestedSyllabus,
  activities: any[],
  equivalencies: Array<any>,
  student: any | undefined,
  repeatEmail: string,
  rules: any[],
  studentRecord: undefined | Profile,
  sourceInstitutionWasSelected: boolean,
  sourceInstitutionIsNew: boolean,
  sourceSyllabusIsNew: boolean,
  sourceSyllabusWasSelected: boolean,
  targetSyllabusWasSelected: boolean,
  studentDataNedded: boolean,
  studentIsComplete: boolean,
  canSaveStudentData: boolean,
  allActivitiesWasEnriched: boolean,
  hasSelectedActivities: boolean,
  hasDocuments: boolean,
  hasDuFrontAndBackValidated: boolean,
  hasStudyPlanAndAcademicReport: boolean,
  thirdStepValidation: boolean,
  duFront: null | AppFile,
  duBack: null | AppFile,
  syllabusFile: null | AppFile,
  academicReport: null | AppFile,
  sourceActivitiesNew: Activity[] | null,
  termsAndConditionsChecked: boolean,
  allReadySaved: boolean,
  dealId: string,
  contactId: string | null,
  proposals: Proposal[],
  incompleteProposals: any,
  setTermsAndConditionsChecked: (value: boolean) => void,
  addSourceActiviyNew: (value: Activity) => void,
  resetRequest: () => void,
  setTargetSyllabus: (value: Syllabus | SuggestedSyllabus | null) => void,
  setSourceSyllabus: (value: Syllabus | null) => void,
  setSourceInstitution: (value: Institution | null) => void,
  setStudent: (value: Student) => void,
  setContactId: (value: any) => void,
  setDealId: (value: any) => void,
  setRepeatEmail: (value: string) => void,
  setActivities: (value: Activity[]) => void,
  setEquivalencies: (value: Array<any> | null) => void,
  setSyllabusFile: (value: AppFile | null) => void,
  setAcademicReport: (value: AppFile | null) => void,
  setCanSaveStudentData: (value: boolean) => void,
  saveStudentRequest: (value: any) => void
  handleNextStep: (value: any) => Promise<any>
  handleBackStep: (value: any) => void,
  setSourceActivitiesNew: (value: any) => void,
  sendDealData: (value?: string) => void,
  setProposals: (value: Proposal[]) => void,
  setIncompleteProposals: (value: any) => void,
  setAllSourceActivitiesMatchedWithTarget: (value: boolean) => void,
  setDuFront: (value: AppFile | null) => void,
  setDuBack: (value: AppFile | null) => void,
  duFrontValidated: boolean;
  duBackValidated: boolean;
  duFrontFileName: string | null;
  duBackFileName: string | null;
  setDuFrontFileName: (fileName: string | null) => void;
  setDuBackFileName: (fileName: string | null) => void;
  duFrontValidationResult: RuleDniValidationInterface | null;
  duBackValidationResult: RuleDniValidationInterface | null;
  setDuFrontValidationResult: (result: RuleDniValidationInterface | null) => void;
  setDuBackValidationResult: (result: RuleDniValidationInterface | null) => void;
  setDuFrontValidated: (validated: boolean) => void;
  setDuBackValidated: (validated: boolean) => void;
  previousRequestFlag: boolean;
  setPreviousRequestFlag: (value: boolean) => void;
}

export const RequestInitialState: RequestContext = {
  source_institution: null,
  source_syllabus: null,
  target_syllabus: null,
  activities: [],
  equivalencies: [],
  student: undefined,
  repeatEmail: '',
  rules: [],
  studentRecord: undefined,
  sourceInstitutionWasSelected: false,
  sourceInstitutionIsNew: false,
  sourceSyllabusIsNew: false,
  sourceSyllabusWasSelected: false,
  targetSyllabusWasSelected: false,
  studentDataNedded: true,
  studentIsComplete: false,
  canSaveStudentData: false,
  allActivitiesWasEnriched: false,
  hasSelectedActivities: false,
  hasDocuments: false,
  hasDuFrontAndBackValidated: false,
  hasStudyPlanAndAcademicReport: false,
  thirdStepValidation: false,
  duFront: null,
  duBack: null,
  syllabusFile: null,
  academicReport: null,
  termsAndConditionsChecked: false,
  allReadySaved: false,
  dealId: '',
  contactId: null,
  proposals: [],
  incompleteProposals: [],
  previousRequestFlag: false,
  resetRequest: () => { },
  setTargetSyllabus: () => { },
  setSourceSyllabus: () => { },
  setSourceInstitution: () => { },
  setStudent: () => { },
  setContactId: () => { },
  setDealId: () => { },
  setRepeatEmail: () => { },
  setActivities: () => { },
  setEquivalencies: () => { },
  setDuFront: () => { },
  setDuBack: () => { },
  setSyllabusFile: () => { },
  setAcademicReport: () => { },
  setCanSaveStudentData: () => { },
  saveStudentRequest: () => { },
  handleNextStep: async () => { },
  handleBackStep: () => { },
  setSourceActivitiesNew: () => { },
  sourceActivitiesNew: [],
  addSourceActiviyNew: () => { },
  setTermsAndConditionsChecked: () => { },
  sendDealData: () => { },
  setProposals: () => { },
  setIncompleteProposals: () => { },
  setAllSourceActivitiesMatchedWithTarget: () => { },
  duFrontValidated: false,
  duBackValidated: false,
  duFrontFileName: null,
  duBackFileName: null,
  setDuFrontFileName: () => { },
  setDuBackFileName: () => { },
  duFrontValidationResult: null,
  duBackValidationResult: null,
  setDuFrontValidationResult: () => { },
  setDuBackValidationResult: () => { },
  setDuFrontValidated: () => { },
  setDuBackValidated: () => { },
  setPreviousRequestFlag: () => { }
};

export const useAcademicTranscriptIsComplete = () => {
  const { isValidApprovalDate } = useDataValidations();
  const validate = (item: any) => {
    return [
      item?.academic_transcript?.book,
      item?.academic_transcript?.calification,
      isValidApprovalDate(item?.academic_transcript?.approval_date),
      item?.academic_transcript?.folder
    ].every(i => !!i);
  }
  return validate
}

export const getSourceSyllabus = (syllabus: any, sourceInstitution: any) => {
  if (!syllabus) {
    return {
      id: null,
      name: null
    };
  }

  if (!sourceInstitution?.id) {
    return {
      id: null,
      name: syllabus.name,
      institution: {
        id: null,
        name: sourceInstitution.name
      }
    };
  }

  if (!syllabus.id) {
    return {
      id: null,
      name: syllabus.name,
      institution: {
        id: sourceInstitution.id,
        name: null
      }
    };
  }

  return {
    id: syllabus.id,
    name: null,
    institution: {
      id: sourceInstitution?.id,
      name: null
    }
  };
};

export const getStudentRecord = (currentStudent: StudentRecord, student: Student) => {
  if (!currentStudent?.id) {
    return {
      id: student?.id || null
    };
  }
  return {
    id: currentStudent.id
  };
};

const mapActivities = (activity: any) => {
  const newObj = {
    id: activity.relationId,
    academic_transcript: {
      book: activity.academic_transcript?.book,
      page: activity.academic_transcript?.folder,
      other: ''
    },
    approval_date: activity.academic_transcript?.approval_date + 'T23:59:59.000Z',
    grade: activity.academic_transcript?.calification,
    state: 'PENDING',
    source: '',
    program_file: activity.program_file,
    activity: {
      ...(activity.new
        ? {
          id: null,
          name: activity.name
        }
        : {
          id: activity.id,
          name: null
        })
    }
  };

  return newObj;
};

export const RequestContextState = createContext(RequestInitialState);

const getFiles = (duFront: AppFile | null, duBack: AppFile | null, academicReport: AppFile | null) => {
  const needDocumentation = duFront && duBack && academicReport;
  return needDocumentation ? [duFront, duBack, academicReport] : [];
};

interface ContextProviderProps {
  children: React.ReactElement
  studentRecord: Profile | undefined,
  studentRequest: Student | null,
  recaptcha: RecaptchaService,
  location: Location,
  dispatch: any
  validations: UseDataValidations,
  settings: SettingStore
}

export const RequestProvider: React.FC<ContextProviderProps> = ({ children, studentRecord, studentRequest, recaptcha, validations, location, dispatch, settings }: any) => {
  const { callIfHumanAsync, reCaptchaToken, generateToken } = recaptcha;
  const [state, setState] = useState(RequestInitialState);
  const [sourceActivitiesNew, setSourceActivitiesNew] = useState<Activity[]>([]);
  const [allReadySaved, setAllReadySaved] = useState(false)
  const { compareEqualsObjects } = validations;
  const { isInternal } = useRequestType();
  const targetInstitution = useAppSelector(state => state.request.targetInstitution);
  const { draftEnable } = useAppSelector((state) => state.settings);
  const callHubspot = settings?.callHubspot
  const validate = useAcademicTranscriptIsComplete()
  const [uid, setUid] = useState(uuidv4());
  const [allSourceActivitiesMatchedWithTarget, setAllSourceActivitiesMatchedWithTarget] = useState(false);
  const [duFrontFileName, setDuFrontFileName] = useState<string | null>(null);
  const [duBackFileName, setDuBackFileName] = useState<string | null>(null);
  const { isExternal } = useRequestType();

  const { user } = useAppContext();
  const request: any = {
    id: uid,
    state: (isExternal && draftEnable) ? RequestState.draft : RequestState.pending,
    reCaptchaToken,
    student: getStudentRecord(studentRecord, state.student),
    syllabus: getSourceSyllabus(state.source_syllabus, state.source_institution),
    target_syllabus: {
      id: state.target_syllabus?.id
    },
    activities: state.activities.map(mapActivities) || [],
    files: getFiles(state.duFront, state.duBack, state.academicReport),
    deal_id: state.dealId,
    proposals: state.proposals
  };

  if (state.syllabusFile) request.files.push(state.syllabusFile);

  const sourceInstitutionWasSelected = !!state?.source_institution;

  const sourceInstitutionIsNew = request.source_syllabus?.institution?.id === '' || state.source_institution?.id === '';

  const sourceSyllabusIsNew = state?.source_syllabus?.id === '';

  const sourceSyllabusWasSelected = !!state?.source_syllabus;

  const targetSyllabusWasSelected = !!state.target_syllabus;

  const studentDataNedded = !studentRecord?.id || typeof request.student?.id !== 'string';

  const studentIsComplete = !!request.student?.id;

  const hasSelectedActivities = state.activities?.length > 0;

  let hasDocuments
  let hasDuFrontAndBackValidated
  let hasStudyPlanAndAcademicReport
  if (settings?.rekognitionStatus >= 0) {
    hasDuFrontAndBackValidated =
      !!state.duFrontValidationResult &&
      state.duFrontValidationResult.trafficLight >= settings.rekognitionStatus &&
      !!state.duBackValidationResult &&
      state.duBackValidationResult.trafficLight >= settings.rekognitionStatus;
    hasStudyPlanAndAcademicReport = !!state.academicReport && !!state.syllabusFile
    hasDocuments = hasDuFrontAndBackValidated && hasStudyPlanAndAcademicReport
  } else {
    hasDuFrontAndBackValidated = !!state.duBack && !!state.duFront
    hasStudyPlanAndAcademicReport = !!state.academicReport && !!state.syllabusFile
    hasDocuments = hasDuFrontAndBackValidated && hasStudyPlanAndAcademicReport
  }
  hasDocuments = user?.valid ? true : hasDocuments

  const allActivitiesWasEnriched = [
    state.activities.length > 0,
    state.activities.every(eq => {
      return validate(eq)
    })
  ].every(Boolean)

  const thirdStepValidation = targetSyllabusWasSelected && state.termsAndConditionsChecked && allActivitiesWasEnriched && hasDocuments && (allSourceActivitiesMatchedWithTarget || isInternal) && (hasStudyPlanAndAcademicReport || isInternal)

  const addSourceActiviyNew = (activity: Activity) => {
    const newActivities = sourceActivitiesNew;
    newActivities.push(activity);
    setSourceActivitiesNew(newActivities);
  };

  const setSourceInstitution = (institution: Institution | null) => {
    dispatch(resetCompatibilityTargetSyllabuses())
    dispatch(resetCardinality())
    setState(prev => ({
      ...prev,
      source_syllabus: null,
      target_syllabus: null,
      activities: [],
      equivalencies: [],
      proposals: [],
      incompleteProposals: [],
      source_institution: institution
    }));
  };

  const setSourceSyllabus = (syllabus: Syllabus | null) => {
    dispatch(resetCompatibilityTargetSyllabuses())
    dispatch(resetCardinality())
    setState(prev => ({
      ...prev,
      source_syllabus: syllabus,
      target_syllabus: null,
      activities: [],
      proposals: [],
      incompleteProposals: []
    }));
  };

  const setTargetSyllabus = (syllabus: any) => {
    dispatch(resetCardinality())
    setState(prev => ({
      ...prev,
      target_syllabus: syllabus,
      equivalencies: [],
      proposals: [],
      incompleteProposals: []
    }));
  };

  const setStudent = (student: any) => {
    setState(prev => ({
      ...prev,
      student
    }));
  };

  const setContactId = (contactId: any) => {
    setState(prev => ({
      ...prev,
      contactId
    }));
  };

  const setDealId = (dealId: any) => {
    setState(prev => ({
      ...prev,
      dealId
    }));
  };

  const setRepeatEmail = (repeatEmail: string) => {
    setState(prev => ({
      ...prev,
      repeatEmail
    }));
  };

  const setEquivalencies = (equivalencies: any) => {
    setState(prev => ({
      ...prev,
      equivalencies
    }));
  };

  const setActivities = (activities: any) => {
    setState(prev => ({
      ...prev,
      activities: activities.map((activity: Activity) => {
        const relationId = uuidv4()
        return {
          ...activity,
          relationId: activity.relationId ?? relationId
        }
      })
    }));
  };

  const setProposals = (proposals: Proposal[]) => {
    setState(prev => ({
      ...prev,
      proposals
    }));
  };

  const setIncompleteProposals = (incompleteProposals: any) => {
    setState(prev => ({
      ...prev,
      incompleteProposals
    }));
  };

  const setDuFront = (duFront: AppFile | null) => {
    setState(prev => ({
      ...prev,
      duFront
    }));
  };

  const setDuBack = (duBack: AppFile | null) => {
    setState(prev => ({
      ...prev,
      duBack
    }));
  };
  const setDuFrontValidated = (validated: boolean) => {
    setState(prevState => ({
      ...prevState,
      duFrontValidated: validated
    }));
  };
  const setDuBackValidated = (validated: boolean) => {
    setState((prevState) => ({
      ...prevState,
      duBackValidated: validated
    }));
  };
  const setDuFrontValidationResult = (result: RuleDniValidationInterface | null) => {
    setState(prevState => ({
      ...prevState,
      duFrontValidationResult: result
    }));
  };
  const setDuBackValidationResult = (result: RuleDniValidationInterface | null) => {
    setState(prevState => ({
      ...prevState,
      duBackValidationResult: result
    }));
  };

  const setSyllabusFile = (syllabusFile: AppFile | null) => {
    setState(prev => ({
      ...prev,
      syllabusFile
    }));
  };

  const setCanSaveStudentData = (canSaveStudentData: boolean) => {
    setState(prev => ({
      ...prev,
      canSaveStudentData
    }));
  };

  const setAcademicReport = (academicReport: AppFile | null) => {
    setState(prev => ({
      ...prev,
      academicReport
    }));
  };

  const setTermsAndConditionsChecked = (termsAndConditionsChecked: boolean) => {
    setState(prev => ({
      ...prev,
      termsAndConditionsChecked
    }));
  }

  const setPreviousRequestFlag = (previousRequestFlag: boolean) => {
    setState(prev => ({
      ...prev,
      previousRequestFlag
    }));
  }
  const resetRequest = () => {
    setState(RequestInitialState);
    setSourceActivitiesNew([]);
    dispatch(resetSourceActivities());
    setUid(uuidv4())
    setAllReadySaved(false)
  };

  const saveStudentRequest = (callback: any) => callIfHumanAsync(async () => {
    setAllReadySaved(true)
    return await callback(request);
  });

  const saveStudentData = () => callIfHumanAsync(async () => {
    const student = state.student;
    const data = { ...student, id: uuidv4(), student_records: [], reCaptchaToken };
    return await dispatch(saveStudent(data)).unwrap();
  });

  const handleNextStudentDataStep = async () => {
    const student = state.student;
    if (student?.id) {
      const areEqualsObjects = compareEqualsObjects(student, studentRequest);
      if (!areEqualsObjects) {
        return await saveStudentData();
      }
    } else {
      return await saveStudentData();
    }
  };

  const sendDealData = async (stageName?: string) => {
    const dealBody: DealData = {
      name: state?.target_syllabus?.extra_content?.program.description ?? null,
      program_id: state?.target_syllabus?.extra_content?.program?.id ?? null,
      modality_external_id: state.target_syllabus?.extra_content?.studyClass?.attendanceModes[0].id ?? null,
      equivalence: true,
      require_equivalencies: true,
      contact_id: state.contactId,
      stage_name: stageName ?? 'CARRERA_DE_INTERES',
      syllabus_id: state.target_syllabus ? ('targetSyllabusId' in state.target_syllabus ? state?.target_syllabus.targetSyllabusId : state?.target_syllabus.id) : null,
      deal_id: state?.dealId || null
    };

    try {
      await dispatch(fetchHubspotDeal(dealBody));
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (location.state && location.state?.sourceInstitution !== '') {
      setSourceInstitution(location.state.sourceInstitution);
    }
    if (location.state && location.state?.sourceSyllabus !== '') {
      setSourceSyllabus(location.state.sourceSyllabus);
      location.state.sourceSyllabus = '';
    }
    if (location.state && location.state?.targetSyllabus !== '') {
      setTargetSyllabus(location.state.targetSyllabus);
      location.state.targetSyllabus = '';
    }
  }, []);

  useEffect(() => {
    if (hasDocuments && callHubspot) sendDealData('DOCUMENTACION_PERSONAL')
  }, [hasDocuments])

  const formatStudentRecordCityData = (address: StudentRecordAddress) => {
    const locationStudent = address?.location;
    const stateStudent = address?.location?.province;
    const countryStudent = address?.location?.province?.country;
    const newCity = {
      id: locationStudent?.id,
      name: locationStudent?.name,
      state: {
        id: stateStudent?.id,
        name: stateStudent?.name,
        country_id: countryStudent?.id,
        country: {
          id: countryStudent?.id,
          name: countryStudent?.name
        }
      }
    };
    return newCity;
  };

  useEffect(() => {
    if (studentRecord) {
      const newData = {
        name: studentRecord?.name,
        last_name: studentRecord?.lastName,
        email: studentRecord?.email,
        telephone: studentRecord?.phone,
        document_type: studentRecord?.documentType?.name,
        document_number: studentRecord?.documentNumber,
        city: studentRecord?.address ? formatStudentRecordCityData(studentRecord?.address) : null
      };
      setStudent(newData);
    }
  }, [studentRecord]);

  const handleNextStep = async (currentStep: any): Promise<any> => {
    switch (currentStep) {
      case STEPS.STUDENT_DATA: {
        if (callHubspot) {
          const body: HubspotContactParams = {
            email: state.student?.email,
            tipo_de_persona: 'CANDIDATE',
            firstname: state.student?.name,
            lastname: state.student?.last_name,
            tipo_de_documento: state.student?.document_type,
            numero_de_documento: state.student?.document_number,
            city: state.student?.city.name,
            id_ciudad: state.student?.city.id,
            state: state.student?.city.state.name,
            id_provincia: state.student?.city.state.id,
            country: state.student?.city.state.country.name,
            id_pais: state.student?.city.state.country.id,
            phone: state.student?.telephone,
            mobilephone: ''
          }
          await dispatch(fetchHubspotContact(body))
        }
        dispatch(fetchStudentRecordsSyllabuses({ document_type: state.student?.document_type, document_number: state.student?.document_number }))
        return await handleNextStudentDataStep();
      }
      case STEPS.SIMULATION:
        break;
      case STEPS.ACADEMIC_HISTORY:
        if (state.source_syllabus?.id && state.source_institution?.id) {
          const compatibleSyllabusesParams: CompatibleSyllabusSearchParams = {
            institutionId: state.source_institution?.id || '',
            canBeTarget: true,
            compatibilityCheck: true,
            sourceSyllabusId: state.source_syllabus?.id
          };
          const targetActivitiesParams = {
            institutionId: targetInstitution?.id ?? '',
            syllabusId: state.target_syllabus?.id ?? ''
          };
          await dispatch(fetchCompatibilityTargetSyllabuses(compatibleSyllabusesParams)).catch((e: any) => console.log(e))
          await dispatch(fetchTargetSyllabusActivities(targetActivitiesParams)).catch((e: any) => console.log(e))
        }
        break;
      case STEPS.TARGET_SELECTION:
         break;
      case STEPS.DOCUMENT_UPLOAD:
         if (callHubspot) sendDealData('SOLICITUD_EMITIDA')
        return await saveStudentRequest(async (request: EquivalencyRequest) => {
          const requestBody: any = {
            ...request,
            proposals: state.proposals.map((proposal: Proposal) => {
              delete proposal.proposalId
              delete proposal.sourceActivityId
              return proposal
            })
          }
          return await dispatch(saveRequest({ request: requestBody, generateToken })).unwrap()
        }
        );
      default: console.log('next', currentStep);
    }
  };

  const handleBackStep = (currentStep: any) => {
    // Ver si hay que borrar o no la seleccion de target syllabus si se vuelve atras desde target selection
    if (currentStep) {
      // case STEPS.TARGET_SELECTION:
      //   return setTargetSyllabus(null);
      return console.log('back', currentStep);
    }
  };

  const contextValues = useMemo(() => ({
    request,
    ...state,
    sourceActivitiesNew,
    sourceInstitutionWasSelected,
    sourceInstitutionIsNew,
    targetInstitution,
    sourceSyllabusIsNew,
    sourceSyllabusWasSelected,
    targetSyllabusWasSelected,
    studentRecord,
    studentDataNedded,
    studentIsComplete,
    hasSelectedActivities,
    hasDocuments,
    hasDuFrontAndBackValidated,
    hasStudyPlanAndAcademicReport,
    thirdStepValidation,
    allActivitiesWasEnriched,
    allReadySaved,
    addSourceActiviyNew,
    resetRequest,
    setSourceInstitution,
    setTargetSyllabus,
    setSourceSyllabus,
    setStudent,
    setContactId,
    setDealId,
    setRepeatEmail,
    setActivities,
    setSourceActivitiesNew,
    sendDealData,
    setEquivalencies,
    saveStudentRequest,
    setDuFront,
    setDuBack,
    setSyllabusFile,
    setAcademicReport,
    setCanSaveStudentData,
    setTermsAndConditionsChecked,
    setProposals,
    setIncompleteProposals,
    setAllSourceActivitiesMatchedWithTarget,
    handleNextStep,
    handleBackStep,
    duFrontFileName,
    duBackFileName,
    setDuBackValidated,
    setDuFrontValidated,
    setDuFrontFileName,
    setDuBackFileName,
    setDuFrontValidationResult,
    setDuBackValidationResult,
    setPreviousRequestFlag
  }), [request]);

  return (
    <RequestContextState.Provider value={contextValues}>
      {children}
    </RequestContextState.Provider>
  );
};

export const useRequestContext = () => {
  return useContext(RequestContextState);
};
